import React, { useState, useEffect } from 'react';
import { authService, type LoginResponse, type User } from '../services/authService';
import { AuthContext, type UserPermissions } from './AuthContextType';

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize with false to ensure proper redirect on first load
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [permissions, setPermissions] = useState<UserPermissions>({});

  useEffect(() => {
    // Check authentication immediately on mount
    const checkAuth = () => {
      const authenticated = authService.isAuthenticated();
      const currentUser = authService.getCurrentUser();
      const currentPermissions = authService.getPermissions() as UserPermissions || {};

      setIsAuthenticated(authenticated);
      setUser(currentUser);
      setPermissions(currentPermissions);
    };

    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<LoginResponse> => {
    const result = await authService.login({ username, password });
    
    setIsAuthenticated(result.success);
    if (result.success && result.user) {
      setUser(result.user);
      setPermissions(result.permissions as UserPermissions);
      // Tech Watch: trigger background digest check
      import('../services/techWatchService').then(m => m.checkLogin().catch(() => {}));
    }
    return result;
  };

  const logout = () => {
    authService.logout();
    setIsAuthenticated(false);
    setUser(null);
    setPermissions({});
    // Redirect to login page
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, permissions, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};
